// ── Movement defaults ────────────────────────────────────────────────
export const LOCAL_PREDICTION_DEFAULT_WALK_SPEED = 4;
export const LOCAL_PREDICTION_DEFAULT_RUN_SPEED = 8;
export const LOCAL_PREDICTION_DEFAULT_JUMP_VELOCITY = 10;
export const LOCAL_PREDICTION_DEFAULT_SWIM_FAST_SPEED = 5;
export const LOCAL_PREDICTION_DEFAULT_SWIM_SLOW_SPEED = 3;
export const LOCAL_PREDICTION_DEFAULT_SWIM_UPWARD_VELOCITY = 2;
export const LOCAL_PREDICTION_DEFAULT_GRAVITY_Y = -32;

// ── Speed estimation ─────────────────────────────────────────────────
export const LOCAL_PREDICTION_MIN_SPEED = 0.2;
export const LOCAL_PREDICTION_MAX_SPEED = 20;
export const LOCAL_PREDICTION_SPEED_REJECT_THRESHOLD = 30;
export const LOCAL_PREDICTION_SPEED_UPWARD_ADAPT_RATE = 0.2;
export const LOCAL_PREDICTION_SPEED_DOWNWARD_ADAPT_RATE = 0.05;
export const LOCAL_PREDICTION_SPEED_DIRECTION_ALIGNMENT_MIN_DOT = 0.85;
export const LOCAL_PREDICTION_SPEED_VERTICAL_REJECT_THRESHOLD = 1.5;

// ── Timing ───────────────────────────────────────────────────────────
export const LOCAL_PREDICTION_MAX_FRAME_DELTA_S = 1 / 10;
export const LOCAL_PREDICTION_SUBSTEP_DELTA_S = 1 / 60;
export const LOCAL_PREDICTION_REPLAY_COMMAND_MAX_DELTA_S = 1 / 8;
export const LOCAL_PREDICTION_REPLAY_MAX_SUBSTEPS_PER_COMMAND = 12;

// ── Horizontal reconciliation ────────────────────────────────────────
// Widen the moving dead zone so the steady-state 1-command-ahead error
// (~0.13 units, errSq~0.017) sits comfortably inside it. This prevents
// the reconciler from oscillating between correction/no-correction.
export const LOCAL_PREDICTION_MOVING_HORIZONTAL_ERROR_DEAD_ZONE_SQ = 0.25 * 0.25;
export const LOCAL_PREDICTION_IDLE_HORIZONTAL_ERROR_DEAD_ZONE_SQ = 0.08 * 0.08;
export const LOCAL_PREDICTION_HORIZONTAL_SNAP_DISTANCE_SQ = 5.0 * 5.0;
export const LOCAL_PREDICTION_MOVING_HORIZONTAL_CORRECTION_RATE = 3;
export const LOCAL_PREDICTION_IDLE_HORIZONTAL_CORRECTION_RATE = 16;

// ── Vertical reconciliation ──────────────────────────────────────────
// Widen the moving vertical dead zone to absorb typical jump/landing
// prediction discrepancies (0.03-0.2 units).
export const LOCAL_PREDICTION_MOVING_VERTICAL_ERROR_DEAD_ZONE = 0.08;
export const LOCAL_PREDICTION_IDLE_VERTICAL_ERROR_DEAD_ZONE = 0.02;
export const LOCAL_PREDICTION_VERTICAL_SNAP_DISTANCE = 5.0;
export const LOCAL_PREDICTION_MOVING_VERTICAL_CORRECTION_RATE = 5;
export const LOCAL_PREDICTION_IDLE_VERTICAL_CORRECTION_RATE = 26;
export const LOCAL_PREDICTION_VERTICAL_VELOCITY_ADAPT_RATE = 0.35;
export const LOCAL_PREDICTION_VERTICAL_VELOCITY_REJECT_THRESHOLD = 80;

// ── Rotation reconciliation ──────────────────────────────────────────
// Widen the moving rotation dead zone and slow correction rate for
// smoother FPS camera/character rotation. 0.8 rad ≈ 46° dead zone.
export const LOCAL_PREDICTION_MOVING_ROTATION_ERROR_DEAD_ZONE = 0.8;
export const LOCAL_PREDICTION_IDLE_ROTATION_ERROR_DEAD_ZONE = 0.05;
export const LOCAL_PREDICTION_ROTATION_SNAP_ANGLE = 2.5;
export const LOCAL_PREDICTION_MOVING_ROTATION_CORRECTION_RATE = 2.5;
export const LOCAL_PREDICTION_IDLE_ROTATION_CORRECTION_RATE = 12;

// ── Swimming ─────────────────────────────────────────────────────────
export const LOCAL_PREDICTION_SWIMMING_DRAG_FACTOR = 0.05;
export const LOCAL_PREDICTION_WATER_ENTRY_SINKING_FACTOR = 0.8;

// ── Command buffer ───────────────────────────────────────────────────
export const LOCAL_PREDICTION_COMMAND_BUFFER_SIZE = 96;

// ── Event key ────────────────────────────────────────────────────────
export const INPUT_MANAGER_MOVEMENT_PACKET_SENT_EVENT = 'INPUT_MANAGER.MOVEMENT_PACKET_SENT';

// ── Prediction flags ─────────────────────────────────────────────────
export const LOCAL_PREDICTION_FLAG_GROUNDED = 1 << 0;
export const LOCAL_PREDICTION_FLAG_SWIMMING = 1 << 1;

// ── Collider / entity geometry ───────────────────────────────────────
export const LOCAL_PREDICTION_COLLIDER_RADIUS = 0.4;
export const LOCAL_PREDICTION_ENTITY_HEIGHT = 1.5;
export const LOCAL_PREDICTION_FOOT_OFFSET = 0.75;
export const LOCAL_PREDICTION_MIN_FOOT_OFFSET = 0.65;
export const LOCAL_PREDICTION_MAX_FOOT_OFFSET = 0.8;
export const LOCAL_PREDICTION_GROUND_SNAP_DISTANCE = 0.18;
export const LOCAL_PREDICTION_GROUND_HOLD_DISTANCE = 0.38;
export const LOCAL_PREDICTION_GROUND_RELEASE_DISTANCE = 0.4;
export const LOCAL_PREDICTION_GROUNDED_GRACE_S = 0.15;
export const LOCAL_PREDICTION_GROUNDED_UPWARD_RELEASE_VELOCITY = 1.25;
export const LOCAL_PREDICTION_COLLISION_EPSILON = 0.001;

// ── Ground sampling geometry ─────────────────────────────────────────
// Match the server ground sensor instead of the wider wall collider so
// moving jump/land prediction samples the same support footprint.
export const LOCAL_PREDICTION_GROUND_SENSOR_RADIUS = 0.23 * (LOCAL_PREDICTION_ENTITY_HEIGHT / 1.5);
export const LOCAL_PREDICTION_GROUND_SAMPLE_CARDINAL_OFFSET =
  LOCAL_PREDICTION_GROUND_SENSOR_RADIUS - LOCAL_PREDICTION_COLLISION_EPSILON;
export const LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET =
  LOCAL_PREDICTION_GROUND_SAMPLE_CARDINAL_OFFSET * Math.SQRT1_2;
export const LOCAL_PREDICTION_GROUND_SAMPLE_INNER_CARDINAL_OFFSET =
  LOCAL_PREDICTION_GROUND_SAMPLE_CARDINAL_OFFSET * 0.5;
export const LOCAL_PREDICTION_GROUND_SAMPLE_INNER_DIAGONAL_OFFSET =
  LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET * 0.5;
export const LOCAL_PREDICTION_FOOTPRINT_SAMPLES = [
  [0, 0],
  [LOCAL_PREDICTION_GROUND_SAMPLE_INNER_CARDINAL_OFFSET, 0],
  [-LOCAL_PREDICTION_GROUND_SAMPLE_INNER_CARDINAL_OFFSET, 0],
  [0, LOCAL_PREDICTION_GROUND_SAMPLE_INNER_CARDINAL_OFFSET],
  [0, -LOCAL_PREDICTION_GROUND_SAMPLE_INNER_CARDINAL_OFFSET],
  [LOCAL_PREDICTION_GROUND_SAMPLE_INNER_DIAGONAL_OFFSET, LOCAL_PREDICTION_GROUND_SAMPLE_INNER_DIAGONAL_OFFSET],
  [LOCAL_PREDICTION_GROUND_SAMPLE_INNER_DIAGONAL_OFFSET, -LOCAL_PREDICTION_GROUND_SAMPLE_INNER_DIAGONAL_OFFSET],
  [-LOCAL_PREDICTION_GROUND_SAMPLE_INNER_DIAGONAL_OFFSET, LOCAL_PREDICTION_GROUND_SAMPLE_INNER_DIAGONAL_OFFSET],
  [-LOCAL_PREDICTION_GROUND_SAMPLE_INNER_DIAGONAL_OFFSET, -LOCAL_PREDICTION_GROUND_SAMPLE_INNER_DIAGONAL_OFFSET],
  [LOCAL_PREDICTION_GROUND_SAMPLE_CARDINAL_OFFSET, 0],
  [-LOCAL_PREDICTION_GROUND_SAMPLE_CARDINAL_OFFSET, 0],
  [0, LOCAL_PREDICTION_GROUND_SAMPLE_CARDINAL_OFFSET],
  [0, -LOCAL_PREDICTION_GROUND_SAMPLE_CARDINAL_OFFSET],
  [LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET, LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET],
  [LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET, -LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET],
  [-LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET, LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET],
  [-LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET, -LOCAL_PREDICTION_GROUND_SAMPLE_DIAGONAL_OFFSET],
] as const;
